<script setup lang="ts">
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import type { Swiper as SwiperClass } from 'swiper/types'
import TeamMember from '@/components/TeamMembers/TeamMember/TeamMember.vue'
import type { IEmits, IProps } from '@/components/TeamMembers/TeamMembers/TeamMembers.types'
import TeamModal from '@/components/TeamMembers/TeamModal/TeamModal.vue'

withDefaults(defineProps<IProps>(), {
  showMore: 'modal',
  initialSlide: 3,
})

const emit = defineEmits<IEmits>()

const { t } = useI18n()

const memberIndex = ref<number>(-1)
const modules = [Navigation, Pagination, Autoplay]

const autoplayConfig = {
  delay: 5000,
  disableOnInteraction: false,
  pauseOnMouseEnter: true,
  stopOnLastSlide: false,
}

const breakpoints = {
  320: {
    slidesPerView: 1,
    spaceBetween: 20,
  },
  768: {
    slidesPerView: 2,
    spaceBetween: 30,
  },
  1024: {
    slidesPerView: 3,
    spaceBetween: 40,
  },
}

const setCurrentSlide = (swiper: SwiperClass) => {
  const bullets = swiper.pagination.bullets
  if (bullets) {
    bullets.forEach((bullet, index) => {
      if (index === swiper.realIndex) {
        bullet.setAttribute('aria-current', 'true')
      } else {
        bullet.removeAttribute('aria-current')
      }
    })
  }
}
</script>

<template>
  <section
    class="career-team-members"
    role="region"
    aria-label="Zespół rekrutacji"
  >
    <div class="career-team-members__header">
      <UiHeader
        id="team-heading"
        :header="boardTitle"
        position="center"
      />
    </div>

    <p
      v-if="boardLead"
      class="career-team-members__lead"
    >
      {{ boardLead }}
    </p>

    <div
      class="career-team-members__swiperContainer"
      role="group"
      aria-labelledby="team-heading"
    >
      <swiper
        :modules="modules"
        :breakpoints="breakpoints"
        :autoplay="autoplayConfig"
        :pagination="{
          el: '.career-team-members__pagination',
          clickable: true,
          renderBullet: (index, className) => {
            return `<button class='${className}' aria-label='Przejdź do slajdu ${index + 1}'></button>`;
          }
        }"
        :navigation="{
          prevEl: '.career-team-members__prev',
          nextEl: '.career-team-members__next'
        }"
        :initial-slide="initialSlide"
        centered-slides
        loop
        class="career-team-members__swiper"
        @slide-change="(swiper) => setCurrentSlide(swiper)"
      >
        <swiper-slide
          v-for="(member, index) in boardMembers"
          :key="index"
          class="career-team-members__slide"
        >
          <TeamMember
            v-bind="member"
            :show-learn-more-button="true"
            :learn-more-aria-label="`${t('common.learnMoreAbout')} ${member.boardMemberName}`"
            @show-more="memberIndex = index"
          />
        </swiper-slide>
      </swiper>

      <div
        aria-live="polite"
        class="career-team-members__controls"
      >
        <button
          class="career-team-members__prev"
          aria-label="Poprzedni slajd"
        >
          <img
            src="/icons/step/arrow_left.svg"
            class="career-team-members__arrow career-team-members__arrow"
            alt=""
          >
        </button>

        <div
          class="career-team-members__pagination"
          role="tablist"
        />

        <button
          class="career-team-members__next"
          aria-label="Następny slajd"
        >
          <img
            src="/icons/step/arrow_right.svg"
            class="career-team-members__arrow"
            alt=""
          >
        </button>
      </div>
    </div>

    <TeamModal
      v-if="(showMore === 'modal') && (memberIndex > -1)"
      :id="modalAriaLabel"
      :model-value="memberIndex"
      :all-board-members="boardMembers"
      @update:model-value="(val) => {
        emit('update:modelValue', val)
        memberIndex = val
      }"
    />
  </section>
</template>

<style scoped lang="scss">
.career-team-members {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 4.8rem 1.6rem;

  &__header {
    margin-bottom: 3.2rem;

    :deep(.ui-header) {
      font-weight: 500;
      font-size: 4.4rem;
      line-height: 5.7rem;
    }
  }

  &__lead {
    @include body-1-medium;
    max-width: 56rem;
    margin-bottom: 2.9rem;
    text-align: center;
  }

  &__swiperContainer {
    position: relative;
    width: 100%;
  }

  &__swiper {
    width: 100%;
    padding-bottom: 4.8rem;
    overflow: visible;
  }

  &__controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2.4rem;
    width: fit-content;
    margin: 0 auto;
  }

  &__prev,
  &__next {
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    width: 4.8rem;
    height: 4.8rem;
    padding: 0;
    background: transparent;
    border: 1px solid $gray-400;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: $gray-200;
      border-color: $gray-500;
    }
  }

  &__arrow {
    width: 0.9rem;
    height: 0.9rem;
  }

  &__pagination {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: auto;

    :deep(.swiper-pagination-bullet) {
      width: 0.8rem;
      height: 0.8rem;
      margin: 0 !important;
      background: transparent;
      border: 1px solid $purple-300;
      opacity: 1;
    }

    :deep(.swiper-pagination-bullet-active) {
      position: relative;
      width: 0.8rem;
      height: 0.8rem;
      background: $purple-400;
      border: none;
      box-shadow: 0 0 0 1.5px $white, 0 0 0 2.5px $purple-300;
      transform: scale(1.1);
    }
  }
}

@include atXS {
  .career-team-members {
    &__header {
      margin-bottom: 4.8rem;
    }
  }
}

@include atM {
  .career-team-members {
    max-width: 136rem;
    margin: 6rem auto;
    padding: 0;

    &__header {
      margin-bottom: 7rem;
    }

    &__lead {
      @include body-1-medium;
      max-width: 56rem;
      margin-bottom: 7.7rem;
    }

    &__swiper {
      overflow: visible;
    }

    &__controls {
      gap: 3.2rem;
      width: fit-content;
      margin: 2rem auto 0;
    }
  }
}
</style>
